import React from 'react';
import { Divider, Typography } from '@mui/material';
import { Box } from '@mui/system';
import NutritionFactRow from './NutritionFactRow';

type Props = {
  nutritional: {
    addedSugarInG: number;
    calories: number;
    cholesterolInMg: number;
    dietaryFiberInG: number;
    totalCarbsInG: number;
    netCarbsInG: number;
    proteinInG: number;
    saturatedFatInG: number;
    servingsPerContainer: number;
    servingSizeInOz: number;
    sodiumInMg: number;
    sugarsInG: number;
    totalFatInG: number;

    calciumInMg: number;
    ironInMg: number;
    potassiumInMg: number;
    vitaminDInMcg: number;
  };
};

type MaxValueFacts = Exclude<
  keyof Props['nutritional'],
  | 'calories'
  | 'netCarbsInG'
  | 'proteinInG'
  | 'servingsPerContainer'
  | 'servingSizeInOz'
  | 'sugarsInG'
>;

const RECOMMENDED_MAX_DAILY_VALUES: Record<MaxValueFacts, number> = {
  totalFatInG: 78,
  saturatedFatInG: 20,
  cholesterolInMg: 300,
  totalCarbsInG: 275,
  addedSugarInG: 50,
  sodiumInMg: 3000,
  dietaryFiberInG: 28,
  vitaminDInMcg: 20,
  calciumInMg: 1300,
  ironInMg: 18,
  potassiumInMg: 4700,
};

const getPercentDailyValue = (
  fact: keyof Props['nutritional'],
  value: number,
): number | null => {
  if (!RECOMMENDED_MAX_DAILY_VALUES[fact as MaxValueFacts]) {
    return null;
  }

  return Math.round(
    (100 * value) / RECOMMENDED_MAX_DAILY_VALUES[fact as MaxValueFacts],
  );
};

const buildAddedSugarsCopy = (addedSugars: number): string => {
  if (addedSugars < 0.5) {
    return 'Contains 0g Added Sugars';
  }

  if (addedSugars < 1) {
    return 'Includes less than 1g Added Sugars';
  }

  return `Includes ${addedSugars.toFixed(1)}g Added Sugars`;
};

type FactConfig = {
  displayAs: string;
  nutritionalKey: keyof Props['nutritional'];
  unit: string;
  adornment?: string;
  hideAmount?: boolean;
  subFacts?: FactConfig[];
};

const NutritionFactPanel: React.FC<Props> = ({ nutritional }) => {
  const macroFactsSansCalories: FactConfig[] = [
    {
      displayAs: 'Total Fat',
      nutritionalKey: 'totalFatInG',
      unit: 'g',
      subFacts: [
        {
          displayAs: 'Saturated Fat',
          nutritionalKey: 'saturatedFatInG',
          unit: 'g',
        },
      ],
    },
    {
      displayAs: 'Cholesterol',
      nutritionalKey: 'cholesterolInMg',
      unit: 'mg',
    },
    {
      displayAs: 'Sodium',
      nutritionalKey: 'sodiumInMg',
      unit: 'mg',
    },
    {
      displayAs: 'Total Carbohydrate',
      nutritionalKey: 'totalCarbsInG',
      unit: 'g',
      subFacts: [
        {
          displayAs: 'Dietary Fiber',
          nutritionalKey: 'dietaryFiberInG',
          unit: 'g',
        },
        {
          displayAs: 'Net Carbs',
          adornment: '†',
          nutritionalKey: 'netCarbsInG',
          unit: 'g',
        },
        {
          displayAs: 'Total Sugars',
          nutritionalKey: 'sugarsInG',
          unit: 'g',
          subFacts: [
            {
              displayAs: buildAddedSugarsCopy(nutritional.addedSugarInG),
              hideAmount: true,
              nutritionalKey: 'addedSugarInG',
              unit: '',
            },
          ],
        },
      ],
    },
    {
      displayAs: 'Protein',
      nutritionalKey: 'proteinInG',
      unit: 'g',
    },
  ];

  const microFacts: FactConfig[] = [
    {
      displayAs: 'Vitamin D',
      nutritionalKey: 'vitaminDInMcg',
      unit: 'mcg',
    },
    {
      displayAs: 'Calcium',
      nutritionalKey: 'calciumInMg',
      unit: 'mg',
    },
    {
      displayAs: 'Iron',
      nutritionalKey: 'ironInMg',
      unit: 'mg',
    },
    {
      displayAs: 'Potassium',
      nutritionalKey: 'potassiumInMg',
      unit: 'mg',
    },
  ];

  const NutritionFactRowRecursive: React.FC<{
    fact: FactConfig;
    indentLevel?: number;
    isLastInChain?: boolean; // true only if this node AND every ancestor is the last sibling
  }> = ({ fact, indentLevel = 0, isLastInChain = true }) => {
    const { displayAs, nutritionalKey, adornment, subFacts, unit } = fact;
    const hasChildren = !!subFacts?.length;

    // Suppress the divider only for the very last row in the entire tree:
    // no children, and every ancestor (and this node itself) is the last sibling.
    const showDivider = !(isLastInChain && !hasChildren);

    return (
      <React.Fragment key={nutritionalKey}>
        <NutritionFactRow
          fact={displayAs}
          amount={nutritional[fact.nutritionalKey]}
          hideAmount={fact.hideAmount}
          unit={unit}
          adornment={adornment}
          indentLevel={indentLevel}
          pctDailyValue={getPercentDailyValue(
            nutritionalKey,
            nutritional[fact.nutritionalKey],
          )}
        />

        {/* only draw the divider after top-level facts */}
        {showDivider && (
          <Divider sx={{ borderTop: '1px solid black', my: '4px' }} />
        )}

        {subFacts?.map((subFact, i) => (
          <NutritionFactRowRecursive
            key={subFact.nutritionalKey}
            fact={subFact}
            indentLevel={indentLevel + 1}
            isLastInChain={isLastInChain && i === subFacts.length - 1}
          />
        ))}
      </React.Fragment>
    );
  };

  return (
    <Box
      sx={{
        fontFamily: "'Archivo Expanded', sans-serif;",
        padding: '14px 16px',
        borderRadius: '14px',
        border: '1px solid #DAD4BC',
      }}
    >
      <Typography
        sx={{
          fontSize: '26px',
          fontWeight: 800,
        }}
      >
        Nutrition Facts
      </Typography>

      <Typography
        sx={{
          fontSize: '13.5px',
        }}
      >
        <b>
          {nutritional.servingsPerContainer} serving
          {nutritional.servingsPerContainer > 1 ? 's' : ''}
        </b>{' '}
        per container
      </Typography>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          sx={{
            fontSize: '1rem',
            fontWeight: 800,
          }}
        >
          Serving size
        </Typography>
        <Typography
          sx={{
            fontSize: '1rem',
            fontWeight: 800,
          }}
        >
          {Math.round(nutritional.servingSizeInOz)} oz.
        </Typography>
      </Box>

      <Divider sx={{ borderTop: '9px solid black', my: '4px' }} />

      <Typography
        sx={{
          fontSize: '11px',
          fontWeight: 700,
        }}
      >
        Amount per serving
      </Typography>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
        }}
      >
        <Typography
          sx={{
            fontSize: '1rem',
            fontWeight: 800,
          }}
        >
          Calories
        </Typography>
        <Typography
          sx={{
            fontSize: '1.5rem',
            fontWeight: 800,
          }}
        >
          {Math.round(nutritional.calories)}
        </Typography>
      </Box>

      <Divider sx={{ borderTop: '5px solid black', my: '5px' }} />

      <Typography
        sx={{
          textAlign: 'right',
          fontSize: '11px',
          fontWeight: 700,
          padding: '2px 0 3px',
        }}
      >
        % Daily Value*
      </Typography>

      {macroFactsSansCalories.map(
        (
          { displayAs, nutritionalKey, adornment, subFacts, unit, hideAmount },
          i,
        ) => (
          <NutritionFactRowRecursive
            key={nutritionalKey}
            fact={{
              displayAs,
              nutritionalKey,
              adornment,
              subFacts,
              unit,
              hideAmount,
            }}
            isLastInChain={i === macroFactsSansCalories.length - 1}
          />
        ),
      )}

      <Divider sx={{ borderTop: '9px solid black', my: '4px' }} />

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
        }}
      >
        {microFacts.map(({ displayAs, nutritionalKey, unit }, i) => (
          <Box
            key={nutritionalKey}
            sx={{
              display: 'flex',
            }}
          >
            <Typography
              sx={{
                fontSize: '11.5px',
                fontWeight: 800,
                mr: '4px',
              }}
            >
              {displayAs}
            </Typography>
            <Typography
              sx={{
                fontSize: '11.5px',
                mr: '4px',
              }}
            >
              {nutritional[nutritionalKey]}
              {unit}
            </Typography>
            <Typography
              sx={{
                fontSize: '11.5px',
                mr: '4px',
              }}
            >
              {getPercentDailyValue(
                nutritionalKey,
                nutritional[nutritionalKey],
              )}
              %
            </Typography>
            {i < microFacts.length - 1 && (
              <Typography
                sx={{
                  fontSize: '11.5px',
                  mr: '4px',
                }}
              >
                •
              </Typography>
            )}
          </Box>
        ))}
      </Box>

      <Divider sx={{ borderTop: '1px solid black', my: '4px' }} />

      <Typography
        variant={'subtitle1'}
        sx={{
          fontSize: '10px',
          lineHeight: 1.4,
        }}
      >
        *The % Daily Value tells you how much a nutrient in a serving of food
        contributes to a daily diet. 2,000 calories a day is used for general
        nutrition advice. †Net Carbs = Total Carbohydrate minus Dietary Fiber.
      </Typography>
    </Box>
  );
};

export default NutritionFactPanel;
